import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { errorResponse, jsonBody, privateApi, successResponse, tagPaths } from "../util.ts";
import { datetime, datetime_usec, exampleEmail, int, simpleSuccessResponse, user_id } from "./common.ts";

extendZodWithOpenApi(z);

//#region User profile

const UserProfileBaseData = z
    .object({
        user_id,
        last_name: z.string().nullable().optional().describe("Last name").openapi({ example: "Doe" }),
        lang: z.string().nullable().describe("Preferred language").openapi({ example: "en" }),
        avatar: z
            .union([z.literal(""), z.string().url()])
            .nullable()
            .openapi({ example: "https://example.com/avatar.png" }),
        id: int.describe("Profile ID"),
        first_name: z.string().nullable().optional().describe("First name").openapi({ example: "John" }),
        email: z.string().nullable().openapi({ example: exampleEmail }).describe("User's email"),
        // XXX: These are not always optional, but eg. the PATCH request response does not include them
        personal_id_number: z
            .string()
            .nullable()
            .optional()
            .describe("Personal identity number, usually issued by the state")
            .openapi({ example: "1234567890" }),
        phone_number: z
            .string()
            .nullable()
            .optional()
            .describe("Phone number in any format")
            .openapi({ example: "1234567890" }),
    })
    .openapi({ ref: "UserProfileBaseData" });

export const UserProfileData = UserProfileBaseData.extend({
    updated_at: datetime_usec.nullable(),
    created_at: datetime_usec,
}).openapi({ ref: "UserProfileData" });

// https://gitlab.com/warrenio/platform/framework/user-resource-interface/-/blob/000b83775baf6877126e7887a344179602436566/api/resources/users.py#L319-326
const UserProfilePatchRequest = UserProfileBaseData.partial()
    .pick({
        first_name: true,
        last_name: true,
        personal_id_number: true,
        phone_number: true,
        lang: true,
    })
    .openapi({ ref: "UserProfilePatchRequest" });

// Ugh, the response format is inconsistent between endpoints...
const UserProfilePatchResponse = UserProfileBaseData.extend({ updated_at: datetime, created_at: datetime }).openapi({
    ref: "UserProfilePatchResponse",
});

export const User = z
    .object({
        id: user_id,
        cookie_id: z.string(),
        name: z.string().openapi({ example: "user@example.com" }),
        signup_site: z.string().optional().openapi({ example: "Warren" }),
        last_activity: datetime.optional(),
        allow_invoice: z.boolean().nullable().optional(),
        profile_data: UserProfileData,
        intercom_user_hash: z.string().optional(),
        // XXX: Only in Auth0CallbackResponse
        kong_key: z.string().optional(),
    })
    .openapi({ ref: "User" });

//#endregion

//#region User MFA
const UserMfaResponse = z
    .object({
        success: z.boolean().describe("Whether MFA is currently enabled for the authenticated user."),
    })
    .openapi({ ref: "UserMfaResponse" });

const UserMfaActivateResponse = z
    .object({
        ticket_id: z.string(),
        ticket_url: z.string(),
    })
    .openapi({ ref: "UserMfaActivateResponse" });

const UserRoleResponse = z
    .object({
        role: z.enum(["admin", "admin-accountant", "user"]).describe("Authenticated user's effective role."),
    })
    .openapi({ ref: "UserRoleResponse" });
//#endregion

//#region Auth0
const Auth0CallbackBody = z
    .object({
        access_token: z.string(),
        id_token: z.string(),
        scope: z.string(),
        expires_in: z.string(),
        token_type: z.string(),
        state: z.string(),
    })
    .openapi({ ref: "Auth0CallbackBody" });

const Auth0CallbackResponse = z
    .object({
        apikey: z.string(),
        is_sign_up: z.boolean().optional(),
        user: User,
    })
    .openapi({ ref: "Auth0CallbackResponse" });
//#endregion

export const userPaths = tagPaths("user")({
    "/user-resource/user": {
        get: {
            summary: "Get user info",
            description: "Authenticated user can request data-model for themselves.",
            responses: {
                ...successResponse(User),
            },
        },
    },
    "/user-resource/user/profile": {
        patch: {
            summary: "Modify profile info",
            description: "Authenticated user can modify their own profile data.",
            requestBody: jsonBody(UserProfilePatchRequest),
            responses: {
                ...successResponse(UserProfilePatchResponse, "Details updated"),
                // TODO: This is wrong, the error response is a HTTP 400
                ...errorResponse("Failed to update details"),
            },
        },
    },
    "/user-resource/user/role": {
        get: {
            summary: "Get user role",
            description: "Returns the authenticated user's effective role on the platform.",
            responses: {
                ...successResponse(UserRoleResponse),
            },
        },
    },
    "/user-resource/mfa/is_active": {
        get: {
            summary: "Get MFA status",
            responses: {
                ...successResponse(UserMfaResponse),
            },
        },
    },
    "/user-resource/mfa/activate": {
        post: {
            summary: "Activate MFA",
            responses: {
                ...successResponse(UserMfaActivateResponse),
            },
        },
    },
    "/user-resource/mfa/deactivate": {
        post: {
            summary: "De-activate MFA",
            description: "(currently unavailable)",
            deprecated: true,
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/user-resource/auth0_callback": {
        post: {
            summary: `Create API key from Auth0 callback data ${privateApi}`,
            requestBody: jsonBody(Auth0CallbackBody),
            responses: {
                ...successResponse(Auth0CallbackResponse),
            },
        },
    },
    "/user-resource/user/privileges": {
        get: {
            summary: `Get user privileges ${privateApi}`,
            responses: {
                "403": {
                    description: "User is not an administrator",
                    content: { "application/json": { schema: z.object({ message: z.string() }) } },
                },
                "404": {
                    description: "User is an administrator",
                    content: { "application/json": { schema: z.object({ message: z.string() }) } },
                },
                "200": {
                    description: "User is an administrator (currently not used)",
                },
                "502": {
                    description: "User is an administrator (Bad Gateway)",
                },
            },
        },
    },
});
